import React, { useState, useContext, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import useAuthNavigation from '../hooks/useAuthNavigation';
import api from '../api/axios';
import { Building2, Mail, Lock, KeyRound, TrendingUp, Shield, Clock, Sparkles, User } from 'lucide-react';

const RegistroPage = () => {
  const location = useLocation();
  const { loginWithTokens, currentUser } = useContext(AuthContext);
  const { navigateAfterLogin } = useAuthNavigation();

  const [form, setForm] = useState({
    nombre_clinica: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmar_password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  // Si ya hay un usuario autenticado, redirigir al dashboard
  useEffect(() => {
    if (currentUser) {
      navigateAfterLogin(location.state?.from);
    }
  }, [currentUser, navigateAfterLogin, location]);

  const isSecurePassword = (pwd) => {
    // Al menos 8 caracteres, 1 letra, 1 número y 1 carácter especial
    const regex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;
    return regex.test(pwd);
  };

  const validate = (values) => {
    const errs = {};
    if (!values.nombre_clinica || values.nombre_clinica.trim().length < 2) {
      errs.nombre_clinica = 'Ingresa un nombre válido (mínimo 2 caracteres)';
    }
    const emailRegex = /\S+@\S+\.\S+/;
    if (!values.email || !emailRegex.test(values.email)) {
      errs.email = 'Ingresa un email válido';
    }
    if (!values.password || !isSecurePassword(values.password)) {
      errs.password = 'La contraseña debe tener mínimo 8 caracteres, incluir letras, números y al menos un carácter especial';
    }
    if (values.confirmar_password && values.password !== values.confirmar_password) {
      errs.confirmar_password = 'Las contraseñas no coinciden';
    }
    return errs;
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const errs = validate(form);
    setFieldErrors((prev) => ({ ...prev, [name]: errs[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const { nombre_clinica, first_name, last_name, email, password } = form;
    const allTouched = { nombre_clinica: true, first_name: true, last_name: true, email: true, password: true, confirmar_password: true };
    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setTouched(allTouched);
      setFieldErrors(errs);
      setIsLoading(false);
      return;
    }

    try {
      // Llamar al endpoint de registro
      await api.post('/usuarios/auth/register/', {
        nombre_clinica,
        first_name,
        last_name,
        email,
        password,
      });

      // NO intentamos login automático porque la cuenta requiere verificación por email
      // Backend devuelve mensaje de éxito, no tokens.
      
      // Toast de éxito con persistencia para que se vea
      toast.success('¡Registro exitoso! Revisa tu correo para activar la cuenta.', {
        duration: 6000,
      });

      // Redirigir al login
      navigateAfterLogin('/app/login');
    } catch (err) {
      console.error("Registration Error:", err.response?.data);
      
      // Mejor manejo de errores de DRF (Field validations)
      if (err.response?.data && typeof err.response.data === 'object') {
          // Si es un objeto de errores, tomamos el primero
          const data = err.response.data;
          
          // Caso: {"email": ["Error msg"], ...}
          const firstKey = Object.keys(data)[0];
          const firstError = data[firstKey];
          
          if (Array.isArray(firstError)) {
              setError(`${firstKey}: ${firstError[0]}`);
          } else if (typeof firstError === 'string') {
              setError(firstError);
          } else {
              setError('Error en los datos enviados.');
          }
      } else {
          const apiMsg = err?.response?.data?.error || err?.response?.data?.detail;
          const message = apiMsg || 'Error al crear la cuenta';
          setError(message);
      }
      toast.error('No se pudo completar el registro.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ============================================= */}
      {/* SECCIÓN IZQUIERDA - FORMULARIO */}
      {/* ============================================= */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-16 xl:px-20 bg-white">
        <div className="mx-auto w-full max-w-md">
          {/* Logo y título */}
          <div className="text-center mb-8">
            <img
              src="/logo-symbol.png"
              alt="EstetikFlow"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-[#1F7A6F] tracking-tight">
              Únete a EstetikFlow
            </h1>
            <p className="mt-3 text-gray-500 text-base">
              Crea tu cuenta y optimiza tu clínica hoy.
            </p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Mensaje de error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            )}

            {/* Campo Nombre de la Clínica */}
            <div>
              <label htmlFor="nombre_clinica" className="block text-sm font-medium text-gray-700 mb-2">
                Nombre de la Clínica
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Building2 className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="nombre_clinica"
                  name="nombre_clinica"
                  type="text"
                  required
                  className={`block w-full pl-12 pr-4 py-3.5 border ${
                    touched.nombre_clinica && fieldErrors.nombre_clinica 
                      ? 'border-red-400 ring-2 ring-red-100' 
                      : 'border-gray-200'
                  } rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] focus:bg-white transition-all duration-200`}
                  placeholder="Clínica Ejemplo"
                  value={form.nombre_clinica}
                  onChange={(e) => setForm({ ...form, nombre_clinica: e.target.value })}
                  onBlur={handleBlur}
                />
              </div>
              {touched.nombre_clinica && fieldErrors.nombre_clinica && (
                <p className="mt-2 text-sm text-red-500">{fieldErrors.nombre_clinica}</p>
              )}
            </div>

            {/* Campos Nombre y Apellido */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="first_name"
                    name="first_name"
                    type="text"
                    className="block w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] focus:bg-white transition-all duration-200"
                    placeholder="Juan"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Apellido
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="last_name"
                    name="last_name"
                    type="text"
                    className="block w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] focus:bg-white transition-all duration-200"
                    placeholder="Pérez"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Campo Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Correo Electrónico
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className={`block w-full pl-12 pr-4 py-3.5 border ${
                    touched.email && fieldErrors.email 
                      ? 'border-red-400 ring-2 ring-red-100' 
                      : 'border-gray-200'
                  } rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] focus:bg-white transition-all duration-200`}
                  placeholder="admin@clinicademo.cl"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  onBlur={handleBlur}
                />
              </div>
              {touched.email && fieldErrors.email && (
                <p className="mt-2 text-sm text-red-500">{fieldErrors.email}</p>
              )}
            </div>

            {/* Campo Contraseña */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className={`block w-full pl-12 pr-4 py-3.5 border ${
                    touched.password && fieldErrors.password 
                      ? 'border-red-400 ring-2 ring-red-100' 
                      : 'border-gray-200'
                  } rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] focus:bg-white transition-all duration-200`}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  onBlur={handleBlur}
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">Mínimo 8 caracteres, incluye letras, números y un carácter especial.</p>
              {touched.password && fieldErrors.password && (
                <p className="mt-1 text-sm text-red-500">{fieldErrors.password}</p>
              )}
            </div>

            {/* Campo Confirmar Contraseña */}
            <div>
              <label htmlFor="confirmar_password" className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <KeyRound className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmar_password"
                  name="confirmar_password"
                  type="password"
                  className={`block w-full pl-12 pr-4 py-3.5 border ${
                    touched.confirmar_password && fieldErrors.confirmar_password 
                      ? 'border-red-400 ring-2 ring-red-100' 
                      : 'border-gray-200'
                  } rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] focus:bg-white transition-all duration-200`}
                  placeholder="••••••••"
                  value={form.confirmar_password}
                  onChange={(e) => setForm({ ...form, confirmar_password: e.target.value })}
                  onBlur={handleBlur}
                />
              </div>
              {touched.confirmar_password && fieldErrors.confirmar_password && (
                <p className="mt-2 text-sm text-red-500">{fieldErrors.confirmar_password}</p>
              )}
            </div>

            {/* Botón de registro */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center py-4 px-6 border border-transparent rounded-xl text-base font-semibold text-white bg-[#2A9D8F] hover:bg-[#E76F51] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2A9D8F] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg shadow-[#2A9D8F]/30 hover:shadow-xl hover:shadow-[#E76F51]/40 transform hover:-translate-y-0.5 active:translate-y-0"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creando cuenta...
                  </>
                ) : (
                  'Crear Cuenta'
                )}
              </button>
            </div>

            {/* Enlace a login */}
            <div className="text-center">
              <p className="text-sm text-gray-500">
                ¿Ya tienes una cuenta?{' '}
                <Link 
                  to="/app/login" 
                  className="font-semibold text-[#2A9D8F] hover:text-[#E76F51] transition-colors duration-200"
                >
                  Inicia Sesión
                </Link>
              </p>
            </div>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-400">
              © 2025 EstetikFlow. Gestión inteligente para clínicas.
            </p>
          </div>
        </div>
      </div>

      {/* ============================================= */}
      {/* SECCIÓN DERECHA - BRANDING/VISUAL */}
      {/* ============================================= */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[#F4E1D2] to-[#FFE8D6]">
        {/* Patrón decorativo sutil */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#2A9D8F]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-32 left-16 w-48 h-48 bg-[#E76F51]/10 rounded-full blur-3xl"></div>
        </div>

        {/* Contenido del panel derecho */}
        <div className="relative flex flex-col justify-center px-12 xl:px-16 w-full">
          <div className="max-w-md mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full mb-6">
              <Sparkles className="h-4 w-4 text-[#E76F51]" />
              <span className="text-sm font-medium text-gray-700">Comienza gratis</span>
            </div>

            {/* Tarjeta flotante de beneficios */}
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">
                Potencia tu Práctica
              </h3>
              
              <div className="space-y-5">
                <div className="flex items-start group">
                  <div className="flex-shrink-0 w-11 h-11 bg-[#2A9D8F] rounded-xl flex items-center justify-center mr-4 shadow-md shadow-[#2A9D8F]/20 group-hover:scale-105 transition-transform duration-200">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-gray-800 font-semibold mb-1">Control financiero total</h4>
                    <p className="text-gray-500 text-sm">Monitorea ingresos, gastos y rentabilidad en tiempo real.</p>
                  </div>
                </div>

                <div className="flex items-start group">
                  <div className="flex-shrink-0 w-11 h-11 bg-[#2A9D8F] rounded-xl flex items-center justify-center mr-4 shadow-md shadow-[#2A9D8F]/20 group-hover:scale-105 transition-transform duration-200">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-gray-800 font-semibold mb-1">Historias clínicas seguras</h4>
                    <p className="text-gray-500 text-sm">Datos encriptados y respaldos automáticos diarios.</p>
                  </div>
                </div>

                <div className="flex items-start group">
                  <div className="flex-shrink-0 w-11 h-11 bg-[#2A9D8F] rounded-xl flex items-center justify-center mr-4 shadow-md shadow-[#2A9D8F]/20 group-hover:scale-105 transition-transform duration-200">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-gray-800 font-semibold mb-1">Ahorra horas de gestión</h4>
                    <p className="text-gray-500 text-sm">Automatiza tareas repetitivas y enfócate en tus pacientes.</p>
                  </div>
                </div>
              </div>

              {/* Estadística destacada */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-[#2A9D8F]">+500</p>
                    <p className="text-sm text-gray-500">Clínicas confían en nosotros</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-[#E76F51]">4.9★</p>
                    <p className="text-sm text-gray-500">Satisfacción de usuarios</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Frase inspiradora */}
            <p className="mt-8 text-center text-gray-600 italic">
              "Simplifica tu día a día y haz crecer tu clínica."
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistroPage;
